import { Tooltip as MuiTooltip } from '@mui/material';
import { useTheme } from './hooks';
import { overlayScrollbarSx } from './overlayScrollbarSx';
import type { SxProps, Theme } from '@mui/material/styles';
import type { TooltipOwnerState, TooltipProps as MuiTooltipProps } from '@mui/material/Tooltip';

export type TooltipProps = MuiTooltipProps;
export const TOOLTIP_ENTER_DELAY_MS = 250;
export const TOOLTIP_ENTER_NEXT_DELAY_MS = 250;
export const TOOLTIP_LEAVE_DELAY_MS = 0;

export const Tooltip = ({
  slotProps,
  enterDelay = TOOLTIP_ENTER_DELAY_MS,
  enterNextDelay = TOOLTIP_ENTER_NEXT_DELAY_MS,
  leaveDelay = TOOLTIP_LEAVE_DELAY_MS,
  placement = 'top',
  ...rest
}: TooltipProps): JSX.Element => {
  const theme = useTheme();
  const baseTooltipSx: SxProps<Theme> = {
    backgroundColor: theme.app.colors.surface,
    border: `1px solid ${theme.app.colors.border}`,
    color: theme.app.colors.text,
    fontSize: '15px',
    fontWeight: 400,
  };
  const tooltipSlot = slotProps?.tooltip;
  const arrowSlot = slotProps?.arrow;
  const baseArrowSx: SxProps<Theme> = {
    color: theme.app.colors.surface,
    '&::before': {
      border: `1px solid ${theme.app.colors.border}`,
      boxSizing: 'border-box',
    },
  };
  const resolveTooltipSlot = (
    ownerState: TooltipOwnerState,
  ): NonNullable<TooltipProps['slotProps']>['tooltip'] => {
    const resolved = typeof tooltipSlot === 'function' ? tooltipSlot(ownerState) : tooltipSlot;
    const resolvedSx = resolved?.sx;
    const mergedSx = resolvedSx
      ? [
          baseTooltipSx,
          overlayScrollbarSx,
          ...(Array.isArray(resolvedSx) ? resolvedSx : [resolvedSx]),
        ]
      : [baseTooltipSx, overlayScrollbarSx];
    return {
      ...(resolved ?? {}),
      sx: mergedSx as SxProps<Theme>,
    };
  };
  const resolveArrowSlot = (
    ownerState: TooltipOwnerState,
  ): NonNullable<TooltipProps['slotProps']>['arrow'] => {
    const resolved = typeof arrowSlot === 'function' ? arrowSlot(ownerState) : arrowSlot;
    const resolvedSx = resolved?.sx;
    const mergedSx = resolvedSx
      ? [baseArrowSx, ...(Array.isArray(resolvedSx) ? resolvedSx : [resolvedSx])]
      : baseArrowSx;
    return {
      ...(resolved ?? {}),
      sx: mergedSx as SxProps<Theme>,
    };
  };
  return (
    <MuiTooltip
      {...rest}
      enterDelay={enterDelay}
      enterNextDelay={enterNextDelay}
      leaveDelay={leaveDelay}
      placement={placement}
      slotProps={{
        ...slotProps,
        tooltip: resolveTooltipSlot,
        arrow: resolveArrowSlot,
      }}
    />
  );
};
